import { RegisteredComponent } from "@builder.io/sdk-react";
import { SORTING_OPTION } from "../../commonFields";
import { IndexCardsContextBlockProps } from "./IndexContextBlock";
import { IndexCard, LocaleIsoCode } from "@/types";
import {
  retrieveIndexCards,
  getSearchFieldsByVariant,
  SearchField,
} from "@/helpers/api/builder";
import { createSortingMapping } from "@/helpers";

export const indexContextBlockConfig: Omit<RegisteredComponent, "component"> = {
  name: "Index cards context",
  canHaveChildren: true,
  description:
    "Abstraction for MongoDB queries - only server side sorting works here, for more complex sorting, check the sections' sorting.",
  inputs: [
    {
      name: "paginationType",
      type: "enum",
      enum: ["infinite", "expandable"],
    },
    {
      name: "numberOfPaginatedCards",
      type: "number",
      defaultValue: 10,
      helperText:
        "This field indicates how many cards are retrieved when paginating.",
    },
    {
      name: "initialSorting",
      type: "object",
      helperText:
        "Defines the sorting for the first render. Choose the card type, and the fields available will be displayed for the given card type.",
      subFields: SORTING_OPTION,
      localized: false,
    },
    {
      name: "variant",
      type: "enum",
      enum: [
        "Resource",
        "Event",
        "Use Case",
        "Integration",
        "Product",
        "Newsroom",
        "CVE",
      ],
      localized: false,
      required: false,
      helperText:
        "The type of index cards. Determines which fields are searched by the search expression.",
    },
    {
      name: "searchExpression",
      type: "text",
      localized: false,
    },
  ],
};

export const parseFilterQueryParams = (
  cards: {
    id: string;
    data: IndexCard;
  }[],
  searchParams: Record<string, string>,
) => {
  const filterQueryParam: Record<string, string[]> = {};
  for (const category of cards.flatMap(({ data }) =>
    data.categorization?.map(({ parameterName, categories }) => ({
      parameterName,
      categories,
    })),
  )) {
    if (category && searchParams[category.parameterName]) {
      if (!(category.parameterName in filterQueryParam)) {
        filterQueryParam[category.parameterName] =
          searchParams[category.parameterName].split(",");
      }
    }
  }
  return filterQueryParam;
};

export const filterCardsByQuery = (
  cards: { id: string; data: IndexCard }[],
  filterQuery: Record<string, string[]>,
  searchExpressionQuery: string | undefined,
  searchFields: SearchField[],
) => {
  const filteredCards = cards.filter((card) => {
    const satisfiesSearchExpression = searchExpressionQuery
      ? searchFields.some(({ fieldPath }) => {
          const dataKey = fieldPath.replace("data.", "");
          const value = card.data[dataKey as keyof typeof card.data];
          return (
            typeof value === "string" &&
            value.toLowerCase().includes(searchExpressionQuery.toLowerCase())
          );
        })
      : true;
    const satisfiesFilters = Object.keys(filterQuery).length
      ? Object.entries(filterQuery).every(([key, value]) =>
          card.data.categorization?.some(
            (categorization) =>
              categorization.parameterName === key &&
              categorization.categories?.some((category) =>
                value.some((filterCategory) => category === filterCategory),
              ),
          ),
        )
      : true;

    return satisfiesSearchExpression && satisfiesFilters;
  });
  return filteredCards;
};

export const remapProps = async (
  props: IndexCardsContextBlockProps,
  url: string,
  isoLocale: LocaleIsoCode,
  searchParamsPromise: Promise<Record<string, string>>,
) => {
  const searchParams = await searchParamsPromise;
  const searchFields = getSearchFieldsByVariant(props.variant);
  const sortingFn = props.initialSorting
    ? createSortingMapping([props.initialSorting])?.[0]
    : null;
  const { cards } = await retrieveIndexCards(
    url,
    isoLocale,
    [],
    undefined,
    sortingFn,
    searchFields,
  );
  const searchExpressionQueryParam = searchParams["searchTerm"]?.toLowerCase();
  const filterQueryParam = parseFilterQueryParams(cards, searchParams);
  const filteredCards = filterCardsByQuery(
    cards,
    filterQueryParam,
    searchExpressionQueryParam,
    searchFields,
  );

  return {
    ...props,
    cards: filteredCards,
    filters: cards,
  };
};
